/*
 * BLT Cart Recommendations — client-side strip ("the shop assistant").
 *
 * Flow: cart changes -> debounced fetch of the theme-rendered JSON endpoint
 * (GET {root}?section_id=blt-cart-reco-data — the Section Rendering API, so the
 * request rides the shopper's own session; no secrets, nothing to forge) ->
 * validate the payload -> render rows into the [data-blt-reco] shell that
 * snippets/blt-complete-the-kit.liquid ships empty and hidden.
 *
 * Cart v2 (Figma 2763:118 / 2763:491): a vertical list. Each row shows
 * "title — price" + a one-line tagline, and a corner action that toggles
 * ⊕ -> spinner -> "✓ added". Added rows STAY in the list; tapping "added"
 * removes the line again (no confirm). The flip is optimistic: a pending set
 * bridges the gap between the tap and the next payload, so the row never
 * bounces back to + while the cart round-trip is in flight.
 *
 * The rendered actions carry the attributes blt-cart.js already delegates on
 * (data-kit-add / data-kit-variants / data-kit-remove), so the size popup,
 * add-to-cart and removal all run through the existing, proven handlers —
 * this file never talks to /cart itself.
 *
 * Guards (each maps to a failure mode we've hit on this store):
 *   - debounce + AbortController: add-burst events collapse into one trip
 *   - payload shape validation: a bot-challenge page served as HTTP 200 can
 *     never reach JSON.parse — anything unexpected hides the strip quietly
 *   - one silent retry for transient 429s, then fail-silent (cached view kept)
 *   - stale-while-revalidate: cart re-renders wipe the strip's DOM; we repaint
 *     instantly from the session cache, then revalidate in the background
 *   - render defers while the size popup is open (no moving-target buttons)
 *   - window.BLT_RECO_DEBUG = true logs failures that are otherwise silent
 */
(function () {
  'use strict';
  if (window.__bltCartReco) return;
  window.__bltCartReco = true;

  var SECTION_ID = 'blt-cart-reco-data';
  var DEBOUNCE_MS = 350;
  var CACHE_KEY = 'blt:reco';
  var PENDING_TTL = 8000;
  var PLUS_SVG = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>';
  var CHECK_SVG = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true" focusable="false"><path d="M5 12l5 5 9-10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var cache = null;
  try { cache = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null'); } catch (e) { /* ignore */ }
  if (cache && (cache.v !== 1 || !Array.isArray(cache.items))) cache = null;

  var timer = null;
  var ctrl = null;
  // optimistic bridge: handle -> { state: 'adding' | 'removing', t: timestamp }
  var pendingByHandle = {};

  function log() {
    if (window.BLT_RECO_DEBUG && window.console) {
      console.warn.apply(console, ['[blt-reco]'].concat(Array.prototype.slice.call(arguments)));
    }
  }
  function containers() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-blt-reco]'));
  }
  function popupOpen() {
    var dlg = document.querySelector('.blt-kit-size.is-open');
    return !!dlg;
  }
  function pendingFor(handle) {
    var p = pendingByHandle[handle];
    if (!p) return null;
    if (Date.now() - p.t > PENDING_TTL) { delete pendingByHandle[handle]; return null; }
    return p.state;
  }
  function setPending(handle, state) {
    if (!handle) return;
    pendingByHandle[handle] = { state: state, t: Date.now() };
  }
  function reconcilePending() {
    // fresh payload = ground truth; drop any pending mark it already satisfies
    if (!cache) return;
    cache.items.forEach(function (it) {
      var p = pendingByHandle[it.handle];
      if (!p) return;
      if (p.state === 'adding' && it.in_cart) delete pendingByHandle[it.handle];
      if (p.state === 'removing' && !it.in_cart) delete pendingByHandle[it.handle];
    });
  }

  /* -------- fetch + validate -------- */
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(function () { fetchData(false); }, DEBOUNCE_MS);
  }
  function fetchData(isRetry) {
    if (!containers().length) return;
    if (ctrl) ctrl.abort();
    ctrl = ('AbortController' in window) ? new AbortController() : null;
    var base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(base + '?section_id=' + SECTION_ID, {
      signal: ctrl && ctrl.signal,
      headers: { Accept: 'text/html' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('http ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-blt-reco-json]');
        if (!node) throw new Error('payload missing'); // challenge page / wrong document
        var data = JSON.parse(node.textContent);
        if (!data || data.v !== 1 || !Array.isArray(data.items)) throw new Error('bad payload');
        cache = data;
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch (e) { /* quota — in-memory only */ }
        reconcilePending();
        renderAll();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        log('fetch failed', err && err.message);
        if (!isRetry) { setTimeout(function () { fetchData(true); }, 700); return; }
        if (!cache) hideAll(); // nothing to show — quietly hide, never a broken box
      });
  }
  function hideAll() {
    containers().forEach(function (c) { c.hidden = true; c.classList.remove('is-loading'); });
  }

  /* -------- rendering -------- */
  function el(tag, cls) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    return n;
  }
  function buildRow(it) {
    var li = el('li', 'blt-cr-kit__row');
    li.setAttribute('data-kit-handle', it.handle);

    var a = el('a', 'blt-cr-kit__thumb');
    a.href = it.url;
    a.tabIndex = -1;
    a.setAttribute('aria-hidden', 'true');
    if (it.image) {
      var img = document.createElement('img');
      img.src = it.image;
      img.alt = '';
      img.loading = 'lazy';
      if (it.image_w) img.width = it.image_w;
      if (it.image_h) img.height = it.image_h;
      a.appendChild(img);
    }
    li.appendChild(a);

    var info = el('div', 'blt-cr-kit__info');
    var title = el('a', 'blt-cr-kit__title');
    title.href = it.url;
    title.appendChild(document.createTextNode(it.title + ' — '));
    if (it.offer_price) {
      var now = el('span', 'blt-cr-kit__price-now');
      now.textContent = it.offer_price;
      title.appendChild(now);
    } else {
      title.appendChild(document.createTextNode(it.price));
    }
    info.appendChild(title);
    var subText = it.offer_note || it.tagline;
    if (subText) {
      var sub = el('p', 'blt-cr-kit__sub' + (it.offer_note ? ' blt-cr-kit__sub--offer' : ''));
      sub.textContent = subText;
      info.appendChild(sub);
    }
    li.appendChild(info);

    var state = pendingFor(it.handle);
    var added = it.in_cart;
    if (state === 'adding') added = true;
    if (state === 'removing') added = false;

    if (added) {
      var chip = el('button', 'blt-cr-kit__added');
      chip.type = 'button';
      chip.setAttribute('data-kit-remove', '');
      if (it.line_key) chip.setAttribute('data-line-key', it.line_key);
      chip.setAttribute('data-kit-handle', it.handle);
      chip.setAttribute('aria-label', 'Remove ' + it.title + ' from cart');
      chip.innerHTML = CHECK_SVG + '<span>added</span>';
      if (state === 'adding' && !it.line_key) chip.disabled = true; // key unknown until the payload catches up
      li.appendChild(chip);
      li.classList.add('is-added');
    } else {
      var btn = el('button', 'blt-cr-kit__add');
      btn.type = 'button';
      btn.setAttribute('data-kit-add', '');
      btn.setAttribute('data-kit-handle', it.handle);
      btn.setAttribute('data-kit-variant', it.variant_id);
      if (it.variants && it.variants.length) {
        btn.setAttribute('data-kit-title', it.title);
        btn.setAttribute('data-kit-option', it.option || 'size');
        btn.setAttribute('data-kit-variants', JSON.stringify(it.variants));
        btn.setAttribute('aria-label', 'Select ' + (it.option || 'size').toLowerCase() + ' for ' + it.title);
      } else {
        btn.setAttribute('aria-label', 'Add ' + it.title + ' to cart');
      }
      if (!it.available) { btn.disabled = true; btn.setAttribute('aria-disabled', 'true'); }
      if (state === 'removing') btn.classList.add('is-busy');
      btn.innerHTML = PLUS_SVG;
      li.appendChild(btn);
    }
    return li;
  }
  function buildGhost() {
    var li = el('li', 'blt-cr-kit__row blt-cr-kit__row--ghost');
    li.setAttribute('aria-hidden', 'true');
    var thumb = el('div', 'blt-cr-kit__thumb blt-cr-kit__thumb--ghost');
    var info = el('div', 'blt-cr-kit__info');
    info.appendChild(el('span', 'blt-cr-kit__ghost-bar'));
    info.appendChild(el('span', 'blt-cr-kit__ghost-bar blt-cr-kit__ghost-bar--short'));
    li.appendChild(thumb);
    li.appendChild(info);
    return li;
  }
  function renderSig() {
    // pending flips must repaint even when the payload sig hasn't moved yet
    var p = Object.keys(pendingByHandle).sort().map(function (h) {
      return h + ':' + pendingByHandle[h].state;
    }).join('|');
    return (cache ? cache.sig : '') + '#' + p;
  }
  function renderInto(c) {
    if (!cache) {
      // first-ever paint: show the shimmer while the initial fetch runs
      var t0 = c.querySelector('[data-kit-track]');
      if (t0 && !t0.children.length) {
        t0.appendChild(buildGhost());
        t0.appendChild(buildGhost());
      }
      c.classList.add('is-loading');
      c.hidden = false;
      return;
    }
    if (cache.empty || !cache.items.length) { c.hidden = true; c.classList.remove('is-loading'); return; }
    var track = c.querySelector('[data-kit-track]');
    if (!track) return;
    var sig = renderSig();
    var ghost = track.querySelector('.blt-cr-kit__row--ghost');
    if (c.getAttribute('data-reco-sig') === sig && track.children.length && !ghost) {
      c.hidden = false;
      c.classList.remove('is-loading');
      return; // identical content — no rebuild, no flicker
    }
    var frag = document.createDocumentFragment();
    cache.items.forEach(function (it) { frag.appendChild(buildRow(it)); });
    track.innerHTML = '';
    track.appendChild(frag);
    c.setAttribute('data-reco-sig', sig);
    c.classList.remove('is-loading');
    c.hidden = false;
  }
  function renderAll() {
    if (popupOpen()) { setTimeout(renderAll, 250); return; } // never repaint under an open size popup
    containers().forEach(renderInto);
  }

  /* -------- optimistic flip (capture phase — before blt-cart.js handles it) -------- */
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    var add = e.target.closest('[data-blt-kit] [data-kit-add]');
    if (add && !add.disabled && !add.hasAttribute('data-kit-variants')) {
      // one-tap add flips instantly; sized products flip when the popup add succeeds (below)
      setPending(add.getAttribute('data-kit-handle'), 'adding');
      setTimeout(renderAll, 0);
      return;
    }
    var rm = e.target.closest('[data-blt-kit] [data-kit-remove]');
    if (rm && !rm.disabled) {
      setPending(rm.getAttribute('data-kit-handle'), 'removing');
      setTimeout(renderAll, 0);
    }
  }, true);
  // sized adds confirm through the popup — blt-cart.js announces the handle it added
  document.addEventListener('blt:kit-added', function (ev) {
    var h = ev && ev.detail && ev.detail.handle;
    if (h) { setPending(h, 'adding'); renderAll(); }
  });

  /* -------- triggers -------- */
  document.addEventListener('cart:update', schedule);

  // Cart roots re-render their whole innerHTML on every mutation — repaint the
  // fresh (empty, hidden) shell from cache immediately, then revalidate.
  function observeRoots() {
    document.querySelectorAll('[data-blt-cart-root]').forEach(function (root) {
      if (root.__bltRecoObserved) return;
      root.__bltRecoObserved = true;
      new MutationObserver(function () {
        renderAll();
        schedule();
      }).observe(root, { childList: true });
    });
  }

  function init() {
    if (!containers().length) return;
    observeRoots();
    renderAll();   // instant paint from session cache (or shimmer on first visit)
    schedule();    // then revalidate
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
